import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import { useAtiradores, useEnviarConvite } from '../hooks/useAtiradores'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useEtapas } from '../../etapas/hooks/useEtapas'
import type { ResultadoConvite } from '../services/atiradores.service'

const APP_URL = window.location.origin

const MENSAGEM_PADRAO = `Ola {nome},

Voce esta convidado a participar da 2a Etapa do nosso campeonato de tiro, que sera realizada no sabado e domingo, dias 29 e 30 de agosto de 2026.

Para garantir sua vaga, faca sua inscricao online pelo link abaixo:
{link}

Contamos com sua participacao!`

function digitsOnly(v: string): string {
  return v.replace(/\D/g, '')
}

function substituir(texto: string, alvo: string, valor: string): string {
  return texto.split(alvo).join(valor)
}

function buildWhatsAppLink(telefone: string, mensagem: string): string {
  let digits = digitsOnly(telefone)
  if (digits.length === 10 || digits.length === 11) digits = '55' + digits
  return `https://wa.me/${digits}?text=${encodeURIComponent(mensagem)}`
}

export function EnviarConvitesPage() {
  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selNumeroEtapa, setSelNumeroEtapa] = useState<number | ''>('')
  const [busca, setBusca] = useState('')
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [assunto, setAssunto] = useState('Convite: 2a Etapa do Campeonato - 29 e 30/08/2026')
  const [mensagem, setMensagem] = useState(MENSAGEM_PADRAO)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [resultado, setResultado] = useState<ResultadoConvite | null>(null)

  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const { data: etapasData } = useEtapas({ idcampeonato: selCampeonato || undefined, limit: 200 })
  const { data: atiradoresData, isLoading } = useAtiradores({ nome: busca || undefined, limit: 500 })
  const enviarConvite = useEnviarConvite()

  const etapasNumeroUnicos = useMemo(() => {
    const vistos = new Set<number>()
    const lista: number[] = []
    for (const e of etapasData?.data ?? []) {
      if (!vistos.has(e.numero_etapa)) { vistos.add(e.numero_etapa); lista.push(e.numero_etapa) }
    }
    return lista.sort((a, b) => a - b)
  }, [etapasData])

  useEffect(() => {
    if (!selNumeroEtapa && etapasNumeroUnicos.length > 0) {
      setSelNumeroEtapa(etapasNumeroUnicos.includes(2) ? 2 : etapasNumeroUnicos[0])
    }
  }, [etapasNumeroUnicos, selNumeroEtapa])

  useEffect(() => {
    setSelNumeroEtapa('')
  }, [selCampeonato])

  const atiradores = atiradoresData?.data ?? []
  const linkInscricao = selCampeonato && selNumeroEtapa
    ? `${APP_URL}/inscricao-publica?idcampeonato=${selCampeonato}&numero_etapa=${selNumeroEtapa}`
    : ''

  const todosSelecionados = atiradores.length > 0 && atiradores.every(a => selecionados.has(a.idatirador))

  function toggleTodos() {
    if (todosSelecionados) {
      setSelecionados(new Set())
    } else {
      setSelecionados(new Set(atiradores.map(a => a.idatirador)))
    }
  }

  function toggleUm(id: number) {
    setSelecionados(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const selecionadosComEmail = atiradores.filter(a => selecionados.has(a.idatirador) && a.email)
  const selecionadosSemEmail = atiradores.filter(a => selecionados.has(a.idatirador) && !a.email)

  async function handleEnviar() {
    if (!selCampeonato || !selNumeroEtapa) {
      setAlert({ type: 'error', message: 'Selecione o campeonato e a etapa' })
      return
    }
    if (selecionados.size === 0) {
      setAlert({ type: 'error', message: 'Selecione ao menos um atirador' })
      return
    }
    try {
      const res = await enviarConvite.mutateAsync({
        idatiradores: Array.from(selecionados),
        idcampeonato: selCampeonato,
        numero_etapa: selNumeroEtapa,
        assunto,
        mensagem,
      })
      setResultado(res)
      setAlert({
        type: res.falhas.length ? 'error' : 'success',
        message: `${res.enviados.length} e-mail(s) enviado(s)${res.falhas.length ? `, ${res.falhas.length} falha(s)` : ''}${res.sem_email.length ? `, ${res.sem_email.length} sem e-mail cadastrado` : ''}`,
      })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao enviar convites' })
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Enviar Convite de Inscrição" description="Convide atiradores cadastrados a se inscreverem online numa etapa" />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Campeonato" required>
            <select className={selectClass} value={selCampeonato}
              onChange={e => setSelCampeonato(e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Selecione...</option>
              {campeonatosData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} ({c.temporada})</option>
              ))}
            </select>
          </FormField>
          <FormField label="Etapa" required>
            <select className={selectClass} value={selNumeroEtapa} disabled={!selCampeonato}
              onChange={e => setSelNumeroEtapa(e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Selecione...</option>
              {etapasNumeroUnicos.map(n => (
                <option key={n} value={n}>{n}a Etapa</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Assunto do e-mail">
          <input className={inputClass} value={assunto} onChange={e => setAssunto(e.target.value)} />
        </FormField>

        <FormField label="Mensagem (use {nome} e {link} — sao substituidos para cada atirador)">
          <textarea className={inputClass} rows={7} value={mensagem} onChange={e => setMensagem(e.target.value)} />
        </FormField>

        {linkInscricao && (
          <p className="text-xs text-compumaq-textoSuave">
            Link que sera enviado: <span className="font-mono">{linkInscricao}</span>
          </p>
        )}
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        <div className="flex flex-wrap gap-3 items-end p-4 border-b border-compumaq-dourado/25">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar por nome</label>
            <input className={inputClass} value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nome do atirador" />
          </div>
          <div className="text-sm text-compumaq-textoSuave">
            {selecionados.size} selecionado(s)
            {selecionadosSemEmail.length > 0 && (
              <span className="text-amber-600"> ({selecionadosSemEmail.length} sem e-mail cadastrado)</span>
            )}
          </div>
          <button onClick={handleEnviar} disabled={enviarConvite.isPending || selecionadosComEmail.length === 0}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
            {enviarConvite.isPending ? 'Enviando...' : `Enviar E-mail (${selecionadosComEmail.length})`}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !atiradores.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="font-medium">Nenhum atirador encontrado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={todosSelecionados} onChange={toggleTodos} className="rounded border-compumaq-dourado/25" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Telefone</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">E-mail</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">WhatsApp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {atiradores.map(a => (
                <tr key={a.idatirador} className="hover:bg-compumaq-preto">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selecionados.has(a.idatirador)} onChange={() => toggleUm(a.idatirador)}
                      className="rounded border-compumaq-dourado/25" />
                  </td>
                  <td className="px-4 py-3 font-medium text-compumaq-texto">{a.nome}</td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">{a.telefone ?? '—'}</td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">
                    {a.email ?? <span className="text-amber-600">sem e-mail</span>}
                  </td>
                  <td className="px-4 py-3">
                    {a.telefone && linkInscricao ? (
                      <a href={buildWhatsAppLink(a.telefone, substituir(substituir(mensagem, '{nome}', a.nome), '{link}', linkInscricao))}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:text-green-800 font-medium">
                        Abrir WhatsApp
                      </a>
                    ) : (
                      <span className="text-xs text-compumaq-textoSuave">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {resultado && (resultado.enviados.length > 0 || resultado.falhas.length > 0) && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-2 text-sm">
          <p className="font-medium text-compumaq-texto">Resultado do envio</p>
          {resultado.enviados.length > 0 && (
            <p className="text-green-700">Enviados: {resultado.enviados.map(e => e.nome).join(', ')}</p>
          )}
          {resultado.sem_email.length > 0 && (
            <p className="text-amber-600">Sem e-mail cadastrado: {resultado.sem_email.map(e => e.nome).join(', ')}</p>
          )}
          {resultado.falhas.length > 0 && (
            <p className="text-red-700">Falhas: {resultado.falhas.map(f => `${f.nome} (${f.erro})`).join('; ')}</p>
          )}
        </div>
      )}
    </div>
  )
}
